import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { tenantQuery } from '../../../utils/firestoreHelpers';

import {
  TX_TYPES,
  DEFAULT_LOYALTY_CONFIG,
  validateLoyaltyConfig,
} from '../../../domain/loyaltyCalculations';

export * from '../../../domain/loyaltyCalculations';

// ============================================================
// CONFIG CACHE
// ============================================================

const CONFIG_CACHE_TTL_MS = 5 * 60 * 1000;
const _configCacheMap = new Map();

export const getLoyaltyConfig = async (businessId, forceRefresh = false) => {
  if (!businessId) throw new Error('getLoyaltyConfig: businessId is required');

  const now = Date.now();
  const cached = _configCacheMap.get(businessId);
  if (!forceRefresh && cached && now - cached.cachedAt < CONFIG_CACHE_TTL_MS) {
    return cached.data;
  }

  try {
    const configRef = doc(db, 'loyalty_config', businessId);
    let snap = await getDoc(configRef);

    if (!snap.exists()) {
      try {
        const legacyRef = doc(db, 'config', 'loyalty');
        const legacySnap = await getDoc(legacyRef);
        if (legacySnap.exists()) {
          await setDoc(configRef, {
            ...legacySnap.data(),
            businessId,
            migratedAt: serverTimestamp(),
          });
          snap = await getDoc(configRef);
          console.info('[LoyaltyConfig] config/loyalty → loyalty_config/' + businessId + ' otomatik migrasyon yapıldı.');
        }
      } catch (migErr) {
        console.warn('[LoyaltyConfig] Migrasyon denenirken hata, default config kullanılacak:', migErr);
      }
    }

    let configData;
    if (snap.exists()) {
      const data = snap.data();
      const validation = validateLoyaltyConfig(data);
      if (validation.isValid) {
        configData = data;
      } else {
        console.warn('Firestore loyalty config is invalid, falling back to defaults:', validation.errors);
        configData = DEFAULT_LOYALTY_CONFIG;
      }
    } else {
      configData = DEFAULT_LOYALTY_CONFIG;
    }

    _configCacheMap.set(businessId, { data: configData, cachedAt: now });
    return configData;
  } catch (error) {
    console.error('Error fetching loyalty config, using defaults:', error);
    return DEFAULT_LOYALTY_CONFIG;
  }
};

export const invalidateLoyaltyConfigCache = (businessId) => {
  if (businessId) {
    _configCacheMap.delete(businessId);
  } else {
    _configCacheMap.clear();
  }
};

export const saveLoyaltyConfig = async (config, updatedBy, businessId) => {
  if (!businessId) throw new Error('saveLoyaltyConfig: businessId is required');

  const validation = validateLoyaltyConfig(config);
  if (!validation.isValid) {
    const err = new Error('Invalid loyalty config: ' + validation.errors.join('; '));
    err.code = 'invalid-config';
    err.details = validation.errors;
    throw err;
  }

  const configRef = doc(db, 'loyalty_config', businessId);
  await setDoc(
    configRef,
    {
      ...config,
      businessId,
      updatedAt: serverTimestamp(),
      updatedBy: updatedBy || 'system',
    },
    { merge: true },
  );

  invalidateLoyaltyConfigCache(businessId);
};

// ============================================================
// QUERY HELPERS (read-only)
// ============================================================

export const getLoyaltyHistory = async (customerId, pageSize = 20, lastDoc = null, businessId) => {
  if (!customerId) return { transactions: [], lastDoc: null, hasMore: false };

  try {
    const cursorConstraints = lastDoc ? [startAfter(lastDoc)] : [];
    const q = tenantQuery(
      collection(db, 'loyaltyTransactions'),
      businessId,
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc'),
      ...cursorConstraints,
      limit(pageSize + 1),
    );
    const snap = await getDocs(q);
    const docs = snap.docs;
    const hasMore = docs.length > pageSize;
    const visibleDocs = docs.slice(0, pageSize);
    const transactions = visibleDocs.map((d) => ({ id: d.id, ...d.data() }));
    const newLastDoc = visibleDocs.length > 0 ? visibleDocs[visibleDocs.length - 1] : null;

    return { transactions, lastDoc: newLastDoc, hasMore };
  } catch (error) {
    console.error('Error fetching loyalty history:', error);
    throw error;
  }
};

export const findExpiredEarnTxs = async (customerId, businessId) => {
  if (!customerId) return [];

  try {
    const q = tenantQuery(
      collection(db, 'loyaltyTransactions'),
      businessId,
      where('customerId', '==', customerId),
      where('type', '==', TX_TYPES.EARN),
      where('expiresAt', '<=', Timestamp.now()),
    );
    const snap = await getDocs(q);
    return snap.docs
      .filter((d) => (d.data().remainingPoints || 0) > 0)
      .map((d) => ({ id: d.id, ref: d.ref, data: d.data() }));
  } catch (error) {
    console.error('Error finding expired earn transactions:', error);
    throw error;
  }
};

export const findBillEarnTxs = async (billId, businessId) => {
  if (!billId) return [];

  try {
    const q = tenantQuery(
      collection(db, 'loyaltyTransactions'),
      businessId,
      where('billId', '==', billId),
      where('type', '==', TX_TYPES.EARN),
    );
    const snap = await getDocs(q);
    return snap.docs
      .filter((d) => !d.data().reversedBy)
      .map((d) => ({ id: d.id, ref: d.ref, data: d.data() }));
  } catch (error) {
    console.error('Error finding bill earn transactions:', error);
    throw error;
  }
};

export const findBillRedeemTxs = async (billId, businessId) => {
  if (!billId) return [];

  try {
    const q = tenantQuery(
      collection(db, 'loyaltyTransactions'),
      businessId,
      where('billId', '==', billId),
      where('type', '==', TX_TYPES.REDEEM),
    );
    const snap = await getDocs(q);
    return snap.docs
      .filter((d) => !d.data().reversedBy)
      .map((d) => ({ id: d.id, ref: d.ref, data: d.data() }));
  } catch (error) {
    console.error('Error finding bill redeem transactions:', error);
    throw error;
  }
};

export const findFifoEarnTxs = async (customerId, pointsNeeded, businessId) => {
  if (!customerId || !pointsNeeded || pointsNeeded <= 0) return [];

  try {
    const q = tenantQuery(
      collection(db, 'loyaltyTransactions'),
      businessId,
      where('customerId', '==', customerId),
      where('type', '==', TX_TYPES.EARN),
      orderBy('expiresAt', 'asc'),
    );
    const snap = await getDocs(q);

    const earns = snap.docs
      .filter((d) => {
        const data = d.data();
        return (data.remainingPoints || 0) > 0 && !data.reversedBy;
      })
      .map((d) => ({ id: d.id, ref: d.ref, data: d.data() }));

    const result = [];
    let accumulated = 0;
    for (const earn of earns) {
      result.push(earn);
      accumulated += earn.data.remainingPoints || 0;
      if (accumulated >= pointsNeeded) break;
    }

    return result;
  } catch (error) {
    console.error('Error finding FIFO earn transactions:', error);
    throw error;
  }
};
